import React, { useEffect, useRef, useState } from "react";

import { useAuth } from "../../../AuthProvider/AuthContext";

import { Eye, EyeOff, Phone, Mail } from "lucide-react";

import { PhoneInput } from "react-international-phone";

import logo from "../../../assets/logo.png";
import "react-international-phone/style.css";
import "./Login.css";



const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");

const OTP_RESEND_SECONDS = 60;



function normalizePhoneNumber(value) {

  const normalized = String(value || "").replace(/[\s()-]/g, "");

  return /^\+[1-9]\d{7,14}$/.test(normalized) ? normalized : null;

}



async function readResponse(response) {

  try {

    return await response.json();

  } catch {

    return {};

  }

}



export default function Login({ onClose, onSwitchToSignup }) {

  const { login } = useAuth();



  const [mode, setMode] = useState("phone");

  const [email, setEmail] = useState("");

  const [password, setPassword] = useState("");

  const [phone, setPhone] = useState("+91");

  const [otp, setOtp] = useState("");

  const [otpSent, setOtpSent] = useState(false);

  const [resendCountdown, setResendCountdown] = useState(0);

  const [error, setError] = useState("");

  const [loading, setLoading] = useState(false);

  const [showPass, setShowPass] = useState(false);

  const countdownRef = useRef(null);



  useEffect(() => {

    return () => {

      if (countdownRef.current) {

        clearInterval(countdownRef.current);

      }

    };

  }, []);



  useEffect(() => {

    if (resendCountdown <= 0) {

      if (countdownRef.current) {

        clearInterval(countdownRef.current);

        countdownRef.current = null;

      }

      return;

    }



    countdownRef.current = setInterval(() => {

      setResendCountdown((current) => Math.max(0, current - 1));

    }, 1000);



    return () => {

      if (countdownRef.current) {

        clearInterval(countdownRef.current);

        countdownRef.current = null;

      }

    };

  }, [resendCountdown > 0]);



  function startResendCountdown() {

    setResendCountdown(OTP_RESEND_SECONDS);

  }



  function switchMode(nextMode) {

    setMode(nextMode);

    setError("");

    setOtp("");

    setOtpSent(false);

    setResendCountdown(0);

  }



  async function handleEmailLogin() {

    setError("");


const normalizedEmail = email.trim().toLowerCase();

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

if (!emailRegex.test(normalizedEmail)) {
  setError("Enter a valid email address.");
  return;
}



    if (!password) {

      setError("Password is required for email login.");

      return;

    }



    setLoading(true);



    try {

      const response = await fetch(`${API_BASE}/api/auth/login`, {

        method: "POST",

        headers: { "Content-Type": "application/json" },

        body: JSON.stringify({ email: normalizedEmail, password }),

      });



      const data = await readResponse(response);



      if (response.ok && data.token && data.user) {

        login(data.user, data.token);

        onClose?.();

        return;

      }



      // Preserve the existing admin login path.

      const adminResponse = await fetch(`${API_BASE}/api/admin/login`, {

        method: "POST",

        headers: { "Content-Type": "application/json" },

        body: JSON.stringify({ email: normalizedEmail, password }),

      });



      const adminData = await readResponse(adminResponse);



      if (adminResponse.ok && adminData.token) {

        localStorage.setItem(

          "mbt_admin_auth",

          JSON.stringify({ token: adminData.token })

        );

        onClose?.();

        window.location.assign("/admin");

        return;

      }



      setError(

        data.error ||

          adminData.error ||

          "Login failed. Check your email and password."

      );

    } catch {

      setError("Could not connect to the server. Please try again.");

    } finally {

      setLoading(false);

    }

  }



  async function handleSendOtp() {

    setError("");



    const normalizedPhone = normalizePhoneNumber(phone);



    if (!normalizedPhone) {

      setError("Enter a valid mobile number with the correct country code.");

      return;

    }



    if (resendCountdown > 0) {

      setError(`Please wait ${resendCountdown} seconds before requesting another OTP.`);

      return;

    }



    setLoading(true);



    try {

      const response = await fetch(

        `${API_BASE}/api/auth/whatsapp/send-otp`,

        {

          method: "POST",

          headers: { "Content-Type": "application/json" },

          body: JSON.stringify({ phone: normalizedPhone }),

        }

      );



      const data = await readResponse(response);



      if (!response.ok) {

        throw new Error(data.error || data.message || "Could not send WhatsApp OTP.");

      }



      setPhone(normalizedPhone);

      setOtp("");

      setOtpSent(true);

      startResendCountdown();

      setError("");

    } catch (err) {

      setError(err.message || "Could not send WhatsApp OTP. Please try again.");

    } finally {

      setLoading(false);

    }

  }



  async function handleVerifyOtp() {

    setError("");



    const normalizedPhone = normalizePhoneNumber(phone);

    const enteredOtp = otp.trim();



    if (!normalizedPhone) {

      setError("Enter a valid mobile number.");

      return;

    }



    if (!otpSent) {

      setError("Request an OTP before continuing.");

      return;

    }



    if (!/^\d{6}$/.test(enteredOtp)) {

      setError("Enter the six-digit OTP sent to your WhatsApp.");

      return;

    }



    setLoading(true);



    try {

      const response = await fetch(

        `${API_BASE}/api/auth/whatsapp/verify-otp`,

        {

          method: "POST",

          headers: { "Content-Type": "application/json" },

          body: JSON.stringify({

            phone: normalizedPhone,

            otp: enteredOtp,

          }),

        }

      );



      const data = await readResponse(response);

      const appToken = data.token || data.accessToken;



      if (!response.ok || !appToken || !data.user) {

        throw new Error(

          data.error || data.message || "WhatsApp OTP verification failed."

        );

      }



      // Backend verifies the phone and logs in or creates the account.

      login(data.user, appToken);

      onClose?.();

    } catch (err) {

      setError(err.message || "Could not verify your WhatsApp OTP.");

    } finally {

      setLoading(false);

    }

  }



  function handleKeyDown(event) {

    if (event.key !== "Enter" || loading) return;



    if (mode === "email") {

      handleEmailLogin();

    } else if (otpSent) {

      handleVerifyOtp();

    } else {

      handleSendOtp();

    }

  }



  return (

    <div className="nb-overlay" onClick={onClose}>

      <div

        className="nb-modal"

        onClick={(event) => event.stopPropagation()}

        role="dialog"

        aria-modal="true"

        aria-labelledby="login-title"

      >

        <button

          type="button"

          className="nb-modal__close"

          onClick={onClose}

          aria-label="Close login"

        >

          ×

        </button>

        <section className="nb-signup-panel">
          <div className="nb-signup-panel__inner">
            <div className="nb-signup-brand">
              <img
                src={logo}
                alt="MakeBeeTrip"
                className="nb-signup-brand__image"
              />
            </div>

        <h2 id="login-title" className="nb-modal__title">

          Log in to MakeBeeTrip

        </h2>



        <p className="nb-modal__sub">

          Sign in securely using WhatsApp OTP.

        </p>



        <div className="nb-login-mode-switch">

          <button

            type="button"

            className={`nb-login-mode-button ${mode === "phone" ? "active" : ""}`}

            onClick={() => switchMode("phone")}

          >

            <Phone size={16} aria-hidden="true" />

            WhatsApp OTP

          </button>



          <button

            type="button"

            className={`nb-login-mode-button ${mode === "email" ? "active" : ""}`}

            onClick={() => switchMode("email")}

          >

            <Mail size={16} aria-hidden="true" />

            Email login

          </button>

        </div>



        {error && (

          <p className="nb-modal__error" role="alert">

            {error}

          </p>

        )}



        {mode === "email" ? (

          <>

            <div className="nb-field">

              <label htmlFor="login-email">Email address</label>

              <input

                id="login-email"

                type="email"

                placeholder="you@example.com"

                value={email}

                onChange={(event) => setEmail(event.target.value)}

                onKeyDown={handleKeyDown}

                autoComplete="email"

                disabled={loading}

              />

            </div>



            <div className="nb-field">

              <label htmlFor="login-password">Password</label>

              <div className="password-input-wrap">

                <input

                  id="login-password"

                  type={showPass ? "text" : "password"}

                  placeholder="Your password"

                  value={password}

                  onChange={(event) => setPassword(event.target.value)}

                  onKeyDown={handleKeyDown}

                  autoComplete="current-password"

                  disabled={loading}

                />



                <button

                  type="button"

                  className="password-toggle"

                  onClick={() => setShowPass((current) => !current)}

                  aria-label={showPass ? "Hide password" : "Show password"}

                >

                  {showPass ? <EyeOff size={18} /> : <Eye size={18} />}

                </button>

              </div>

            </div>



            <button

              type="button"

              className="nb-btn nb-btn--primary nb-btn--full nb-modal__submit"

              onClick={handleEmailLogin}

              disabled={loading}

            >

              {loading ? "Logging in…" : "Log in"}

            </button>

            <div className="nb-modal__divider">

  <span>or</span>

</div>



<div className="nb-modal__social">

  <button

    type="button"

    className="nb-social-btn"

    onClick={() => {

      window.location.href = `${API_BASE}/api/auth/google`;

    }}

  >

    <GoogleIcon />

    Continue with Google

  </button>

</div>

          </>

        ) : (

          <>

            <div className="nb-field nb-phone-field">

              <label htmlFor="login-phone">Mobile number</label>



              <PhoneInput

                defaultCountry="in"

                value={phone}

                onChange={(value) => {

                  if (value !== phone) {

                    setPhone(value);

                    setOtp("");

                    setOtpSent(false);

                    setResendCountdown(0);

                  }

                  setError("");

                }}

                preferredCountries={["in", "us", "gb", "ae", "au", "sg"]}

                inputProps={{

                  id: "login-phone",

                  name: "phone",

                  autoComplete: "tel",

                  placeholder: "Enter mobile number",

                  disabled: loading,

                  onKeyDown: handleKeyDown,

                  "aria-label": "Mobile number with country code",

                }}

                style={{ width: "100%" }}

                inputStyle={{

                  width: "100%",

                  minWidth: 0,

                  height: "46px",

                  fontSize: "14px",

                  color: "#0d1b3e",

                  background: "#ffffff",

                  border: "1px solid rgba(13,27,62,0.16)",

                  borderRadius: "0 8px 8px 0",

                  outline: "none",

                  boxSizing: "border-box",

                }}

                countrySelectorStyleProps={{

                  buttonStyle: {

                    width: "68px",

                    minWidth: "68px",

                    height: "46px",

                    padding: "0 7px",

                    display: "flex",

                    alignItems: "center",

                    justifyContent: "center",

                    gap: "4px",

                    background: "#f7f9fc",

                    border: "1px solid rgba(13,27,62,0.2)",

                    borderRight: "none",

                    borderRadius: "8px 0 0 8px",

                    cursor: "pointer",

                    boxSizing: "border-box",

                  },

                }}

              />

            </div>



            {!otpSent ? (

              <>

                <p className="nb-modal__sub">

                  We’ll send a verification code to your WhatsApp number.

                </p>



                <button

                  type="button"

                  className="nb-btn nb-btn--primary nb-btn--full nb-modal__submit"

                  onClick={handleSendOtp}

                  disabled={loading}

                >

                  {loading ? "Sending OTP…" : "Send WhatsApp OTP"}

                </button>

              </>

            ) : (

              <>

                <p className="nb-modal__sub">

                  Enter the six-digit code sent to WhatsApp at{" "}

                  <strong>{normalizePhoneNumber(phone) || phone}</strong>.

                </p>



                <div className="nb-field">

                  <label htmlFor="login-otp">Verification code</label>

                  <input

                    id="login-otp"

                    type="text"

                    inputMode="numeric"

                    autoComplete="one-time-code"

                    placeholder="Enter 6-digit OTP"

                    maxLength={6}

                    value={otp}

                    onChange={(event) => {

                      setOtp(event.target.value.replace(/\D/g, "").slice(0, 6));

                      setError("");

                    }}

                    onKeyDown={handleKeyDown}

                    disabled={loading}

                  />

                </div>



                <button

                  type="button"

                  className="nb-btn nb-btn--primary nb-btn--full nb-modal__submit"

                  onClick={handleVerifyOtp}

                  disabled={loading || otp.length !== 6}

                >

                  {loading ? "Verifying…" : "Verify & Log In"}

                </button>



                <button

                  type="button"

                  className="nb-login-resend"

                  onClick={handleSendOtp}

                  disabled={loading || resendCountdown > 0}

                >

                  {resendCountdown > 0

                    ? `Resend OTP in ${resendCountdown}s`

                    : "Resend WhatsApp OTP"}

                </button>

              </>

            )}

          </>

        )}



        <p className="nb-modal__footer">

          Don’t have an account?{" "}

          <button

            type="button"

            className="nb-login-signup-link"

            onClick={() => {

              onClose?.();

              onSwitchToSignup?.();

            }}

          >

            Sign up

          </button>

        </p>

          </div>
        </section>
      </div>

    </div>

  );

}



function GoogleIcon() {

  return (

    <svg

      width="16"

      height="16"

      viewBox="0 0 24 24"

      aria-hidden="true"

    >

      <path

        fill="#4285F4"

        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"

      />

      <path

        fill="#34A853"

        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"

      />

      <path

        fill="#FBBC05"

        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11 11 0 0 0 1 12c0 1.78.43 3.45 1.18 4.93l2.85-2.22.81-.62z"

      />

      <path

        fill="#EA4335"

        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"

      />

    </svg>

  );

}